import { Fragment } from "react";
import { cn } from "@/lib/utils";
import type { WorkflowStep } from "../state";

const STEPS: { key: WorkflowStep; label: string; index: number }[] = [
  { key: "Upload", label: "Upload", index: 1 },
  { key: "Processing", label: "Process", index: 2 },
  { key: "Review", label: "Review", index: 3 },
  { key: "Download", label: "Download", index: 4 },
];

export function StepIndicator({
  current,
  completed,
}: {
  current: WorkflowStep;
  completed: Set<WorkflowStep>;
}) {
  const effective = current === "Generating" ? "Review" : current;

  return (
    <ol className="flex w-full items-start gap-2">
      {STEPS.map((s, i) => {
        const isActive = s.key === effective;
        const isDone = completed.has(s.key) || isAfter(s.key, effective);
        const isFuture = !isActive && !isDone;
        const nextIsDoneOrActive =
          i < STEPS.length - 1 &&
          (isDone ||
            isActive ||
            (current === "Generating" && s.key === "Review"));

        return (
          <Fragment key={s.key}>
            <li className="flex w-[88px] shrink-0 flex-col items-center gap-2">
              <div
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full text-[0.9rem] font-semibold transition-all duration-300",
                  isActive && "bg-primary text-white shadow-soft ring-4 ring-primary/15",
                  isDone && "bg-sage text-white",
                  isFuture && "bg-surface-2 text-ink-muted border border-rule",
                )}
                aria-current={isActive ? "step" : undefined}
              >
                {isDone ? <CheckGlyph /> : s.index}
              </div>
              <div
                className={cn(
                  "text-[0.82rem] font-medium",
                  isActive && "text-ink",
                  isDone && "text-sage-ink",
                  isFuture && "text-ink-muted",
                )}
              >
                {s.label}
              </div>
            </li>

            {i < STEPS.length - 1 && (
              <li
                aria-hidden
                className={cn(
                  "mt-[18px] h-1 flex-1 rounded-full transition-colors",
                  nextIsDoneOrActive ? "bg-sage" : "bg-rule",
                )}
              />
            )}
          </Fragment>
        );
      })}
    </ol>
  );
}

function isAfter(stepKey: WorkflowStep, currentKey: WorkflowStep) {
  const order: WorkflowStep[] = [
    "Upload",
    "Processing",
    "Review",
    "Generating",
    "Download",
  ];
  return order.indexOf(stepKey) < order.indexOf(currentKey);
}

function CheckGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M3 8.5l3 3L13 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
